// create-task.tsx
import React, { useEffect } from "react";
import { Modal, Button, Row, Col, Container, Form } from "react-bootstrap";
import { Formik, Field } from "formik";
import * as Yup from "yup";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { createTaskAsync } from "../../../../slices/project-management/project-management-slice";
import { getAllDropdownUsersAsync } from "../../../../slices/user/userSlice";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { toast } from "react-toastify";
import errorIcon from "../../../../assets/images/error-icon.svg";

interface CreateTaskValues {
	task_name: string;
	assigned_to: number;
	start_date: string;
	end_date: string;
	task_status: string;
	task_color: string;
}

interface CreateTaskProps {
	closeModal: () => void;
	show: boolean;
	project_id: string | undefined | null;
	onCreateSuccess: () => void;
}

const CreateTask: React.FC<CreateTaskProps> = ({
	closeModal,
	show,
	project_id,
	onCreateSuccess,
}) => {
	const dispatch = useAppDispatch();
	const companyId = localStorage.getItem("userCompanyID");

	const users = useAppSelector((state) => state.user.users?.data);
	console.log("projectId", project_id);
	console.log("projectId", companyId);

	useEffect(() => {
		if (companyId) {
			dispatch(getAllDropdownUsersAsync({ companyId }));
		}
	}, [companyId, dispatch]);

	return (
		<Container fluid className="">
			<Modal
				size="lg"
				show={show}
				onHide={closeModal}
				style={{ maxHeight: "100vh", overflowY: "auto" }}
			>
				<div className="px-5">
					<Formik
						initialValues={{
							task_name: "",
							assigned_to: 0,
							start_date: "",
							end_date: "",
							task_status: "",
							task_color: "",
						}}
						validationSchema={Yup.object().shape({
							task_name: Yup.string().required("Required"),
							assigned_to: Yup.number()
								.required("Required")
								.min(1, "Choose a user"),
							start_date: Yup.string().required("Required"),
							end_date: Yup.date()
								.required("Required")
								.test(
									"is-after-start",
									"End date must be after start date",
									function (value) {
										const { start_date } = this.parent;
										return start_date && value
											? new Date(value) > new Date(start_date)
											: true;
									},
								),
							task_status: Yup.string().required("Required"),
							task_color: Yup.string().required("Required"),
						})}
						onSubmit={(values: CreateTaskValues, { setSubmitting }) => {
							if (companyId) {
								dispatch(
									createTaskAsync({
										...values,
										projectId: `${project_id}`,
										company_id: `${companyId}`,
									}),
								)
									.unwrap()
									.then((response) => {
										onCreateSuccess();
										closeModal();
									})
									.catch((error) => { })
									.catch((error) => {
										const errorMessage = Object.values(error.message)
											.flat()
											.join(" ");
										toast.error(errorMessage);
									})
									.finally(() => {
										setSubmitting(false);
									});
							} else {
								console.error("project_id or company_id is undefined");
							}
						}}
						validateOnChange
						validateOnBlur
					>
						{({
							errors,
							touched,
							handleSubmit,
							isSubmitting,
							values,
							handleChange,
							setFieldValue,
						}) => (
							<Form onSubmit={handleSubmit}>
								<Modal.Header closeButton className="text-start mt-2">
									<h4>Create task</h4>
								</Modal.Header>
								<Modal.Body>
									<Row>
										<Col md={12} className="mt-2 w-50">
											<label className="my-2">
												Task name <span className="text-danger">*</span>
											</label>
											<Field
												type="text"
												name="task_name"
												className={`fluent-input  ${touched.task_name && errors.task_name
													? "is-invalid"
													: ""
													}`}
												required
											></Field>
											{touched.task_name && errors.task_name ? (
												<p className="error-message mb-0 d-inline-flex align-items-center">
													<img src={errorIcon} alt="error-icon" />
													{errors.task_name}
												</p>
											) : (
												<p className="error-message-placeholder mb-0">
													no errors
												</p>
											)}
										</Col>
										<Col md={12} className="mt-2 w-50">
											<label className="my-2">
												Assign task to <span className="text-danger">*</span>
											</label>
											<FormControl fullWidth>
												<InputLabel>Member</InputLabel>
												<Select
													label="Member"
													name="assigned_to"
													className={`fluent-input  ${touched.assigned_to && errors.assigned_to
														? "is-invalid"
														: ""
														}`}
													onChange={handleChange}
													required
												>
													{users !== undefined && users !== null ? (
														users?.map((user) => (
															<MenuItem key={user.id} value={user.id}>
																{user.first_name} {user.last_name}
															</MenuItem>
														))
													) : (
														<MenuItem value="">John Doe</MenuItem>
													)}
												</Select>
											</FormControl>
											{touched.assigned_to && errors.assigned_to ? (
												<p className="error-message mb-0 d-inline-flex align-items-center">
													<img src={errorIcon} alt="error-icon" />
													{errors.assigned_to}
												</p>
											) : (
												<p className="error-message-placeholder mb-0">
													no errors
												</p>
											)}
										</Col>
									</Row>
									<Row>
										<Col md={12} className="mt-2 w-50">
											<label className="my-2">
												Planned start date{" "}
												<span className="text-danger">*</span>
											</label>
											<Field
												type="date"
												name="start_date"
												className={`fluent-input  ${touched.start_date && errors.start_date
													? "is-invalid"
													: ""
													}`}
												required
											></Field>
											{touched.start_date && errors.start_date ? (
												<p className="error-message mb-0 d-inline-flex align-items-center">
													<img src={errorIcon} alt="error-icon" />
													{errors.start_date}
												</p>
											) : (
												<p className="error-message-placeholder mb-0">
													no errors
												</p>
											)}
										</Col>
										<Col md={12} className="mt-2 w-50">
											<label className="my-2">
												Planned end date <span className="text-danger">*</span>
											</label>
											<Field
												type="date"
												name="end_date"
												className={`fluent-input  ${touched.end_date && errors.end_date
													? "is-invalid"
													: ""
													}`}
												required
											></Field>
											{touched.end_date && errors.end_date ? (
												<p className="error-message mb-0 d-inline-flex align-items-center">
													<img src={errorIcon} alt="error-icon" />
													{errors.end_date}
												</p>
											) : (
												<p className="error-message-placeholder mb-0">
													no errors
												</p>
											)}
										</Col>
									</Row>
									<Row>
										<Col md={12} className="mt-2 w-50">
											<label className="my-2">
												Status <span className="text-danger">*</span>
											</label>
											<FormControl fullWidth>
												<InputLabel>Status</InputLabel>
												<Select
													label="task_status"
													name="task_status"
													className={`fluent-input  ${touched.task_status && errors.task_status
														? "is-invalid"
														: ""
														}`}
													onChange={handleChange}
													required
												>
													<MenuItem value="To do">To do</MenuItem>
													<MenuItem value="In Progress">In progress</MenuItem>
													<MenuItem value="Done">Done</MenuItem>
												</Select>
											</FormControl>
											{touched.task_status && errors.task_status ? (
												<p className="error-message mb-0 d-inline-flex align-items-center">
													<img src={errorIcon} alt="error-icon" />
													{errors.task_status}
												</p>
											) : (
												<p className="error-message-placeholder mb-0">
													no errors
												</p>
											)}
										</Col>
										<Col md={12} className="mt-2 w-50">
											<label className="my-2">
												Color <span className="text-danger">*</span>
											</label>
											<Field
												type="color"
												name="task_color"
												className={`fluent-input  ${touched.task_color && errors.task_color
													? "is-invalid"
													: ""
													}`}
												required
											></Field>
											{touched.task_color && errors.task_color ? (
												<p className="error-message mb-0 d-inline-flex align-items-center">
													<img src={errorIcon} alt="error-icon" />
													{errors.task_color}
												</p>
											) : (
												<p className="error-message-placeholder mb-0">
													no errors
												</p>
											)}
										</Col>
									</Row>

									<Modal.Footer className="mt-5">
										<Row>
											<Col md={6}>
												<div className=" mt-1">
													<Button onClick={closeModal} className="cancel-btn">
														<span className="text-center">Cancel </span>{" "}
													</Button>
												</div>
											</Col>
											<Col md={6}>
												<div className=" mt-1">
													<Button
														type="submit"
														className="border-0 create-btn bg-color-1"
														disabled={isSubmitting}
													>
														<span className="text-center">
															{isSubmitting
																? `Creating task...`
																: `Create task`}
														</span>{" "}
													</Button>
												</div>
											</Col>
										</Row>
									</Modal.Footer>
								</Modal.Body>
							</Form>
						)}
					</Formik>
				</div>
			</Modal>
		</Container>
	);
};

export default CreateTask;
